import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Bot, Smartphone, Globe, ArrowRight } from 'lucide-react';
import { featuredServices } from '../mockData';
import { Button } from './ui/button';

const FeaturedServices = () => {
  const icons = { Bot, Smartphone, Globe };

  return (
    <section
      className="relative py-32 bg-[#1A1A1A] overflow-hidden"
      data-testid="featured-services-section"
    >
      {/* Subtle grid texture */}
      <div className="absolute inset-0 opacity-[0.04]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px),
              linear-gradient(rgba(255,255,255,1) 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px',
          }}
        />
      </div>

      {/* Red accent glow */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-[#C8102E] blur-[160px] opacity-20 pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <span className="inline-block text-sm font-medium text-[#C8102E] uppercase tracking-widest mb-4">
            What We Build
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            Solutions built for growth
          </h2>
          <p className="text-lg md:text-xl text-gray-400 leading-relaxed">
            Enterprise-grade digital products engineered to elevate your brand and drive results
          </p>
        </motion.div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {featuredServices.map((service, index) => {
            const Icon = icons[service.icon];
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.12 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
                className="group relative bg-white/[0.03] hover:bg-white/[0.06] backdrop-blur-sm rounded-3xl p-10 border border-white/10 hover:border-[#C8102E]/50 transition-all duration-500"
                data-testid={`featured-service-${service.id}`}
              >
                {/* Icon */}
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#C8102E] to-[#8f0b20] flex items-center justify-center mb-8 shadow-lg shadow-[#C8102E]/20 group-hover:scale-110 transition-transform duration-500">
                  <Icon className="text-white" size={30} />
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 tracking-tight">
                  {service.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {service.description}
                </p>

                {/* Corner accent number */}
                <span className="absolute top-8 right-10 text-5xl font-bold text-white/5 group-hover:text-[#C8102E]/20 transition-colors duration-500">
                  0{service.id}
                </span>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Link to="/contact">
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="inline-block">
              <Button
                className="bg-[#C8102E] hover:bg-white hover:text-[#1A1A1A] text-white px-10 py-6 text-base rounded-full transition-all duration-300 shadow-lg shadow-[#C8102E]/30"
                data-testid="book-discovery-call-button"
              >
                Book a Discovery Call
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </motion.div>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedServices;
